import {
	faArrowDownWideShort as fasArrowDownWideShort,
	faArrowRight as fasArrowRight,
	faCalculatorSimple as fasCalculatorSimple,
	faCircleXmark as fasCircleXmark,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import Ad from "@component/Widget/Ad/Ad";
import type { AbstractResultItemProps } from "@component/assets/AbstractListingCalculator/AbstractResultItem";
import type {
	AbstractResultListProps,
	AbstractResultListState,
	OrderFields,
} from "@component/assets/AbstractListingCalculator/AbstractResultList";
import { AbstractResultList } from "@component/assets/AbstractListingCalculator/AbstractResultList";
import { getBankName } from "@component/assets/data/banks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText, sprintf } from "@ui/format";
import autobind from "autobind-decorator";
import {
	Alert,
	Button,
	CollapseBlock,
	FormCheck,
	FormCheckList,
	FormSelect,
	HtmlBlock,
	LandingContainer,
	List,
	__,
	getLocale,
} from "bm-ui-kit";
import type { CommonOptions, FormCheckOptions } from "bm-ui-kit";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Col, Collapse, Container, Form, Row } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";
import { MortgageLeadForm } from "../LeadForm/MortgageLeadForm";
import type { Props as MortgageResultItemProps } from "./MortgageResultItem";
import { MortgageResultItem } from "./MortgageResultItem";
import { getLeadCalculatorRequest, getResultsInPeriod, hasResultInPeriod, isCSOKVariant } from "./domain";
import type {
	MortgageListingCalculatorVariant,
	MortgageRequest,
	MortgageResponseItem,
	MortgageSearchParams,
	MortgageSort,
	PeriodCategory,
} from "./types";

const INTEREST_FILTER_OPTIONS: FormCheckOptions<PeriodCategory> = [
	{
		label: `15-30 ${__("év")}`,
		value: "FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS",
		infoTooltip: "A jövőbeni kamatemelkedés KIS MÉRTÉKBEN MEGNÖVELHETI a hitel havi törlesztőrészletét.",
	},
	{
		label: `10 ${__("év")}`,
		value: "TEN_YEARS",
		infoTooltip: "A jövőbeni kamatemelkedés MÉRSÉKELTEN MEGNÖVELHETI a hitel havi törlesztőrészletét.",
	},
	{
		label: __("Végig fix*"),
		value: "FULL",
		infoTooltip: "A jövőbeni kamatemelkedés NEM NÖVELHETI a hitel havi törlesztőrészletét.",
	},
	{
		label: `5 ${__("év")}`,
		value: "FIVE_YEARS",
		infoTooltip: "A jövőbeni kamatemelkedés MEGNÖVELHETI a hitel havi törlesztőrészletét.",
	},
	{
		label: __("Változó kamat"),
		value: "THREE_MONTHS,SIX_MONTHS,ONE_YEAR,THREE_YEARS",
		infoTooltip: "A jövőbeni kamatemelkedés SZÁMOTTEVŐ MÉRTÉKBEN MEGNÖVELHETI a hitel havi törlesztőrészletét.",
	},
];

const GROUP_LIMIT = 3;

interface Props
	extends AbstractResultListProps<MortgageSearchParams, MortgageSort, MortgageRequest, MortgageResponseItem> {
	onlyCt?: boolean;
	variant: MortgageListingCalculatorVariant;
}

interface State extends AbstractResultListState {
	grouping: boolean;
	interestFilter: PeriodCategory[];
	interestFilterOptions: FormCheckOptions<PeriodCategory>;
}

export interface MortgageOrderFields extends OrderFields<MortgageSort> {
	grouping: boolean;
	interestFilter: PeriodCategory[];
}

export class MortgageResultList extends AbstractResultList<
	MortgageSearchParams,
	MortgageSort,
	MortgageRequest,
	MortgageResponseItem,
	Props,
	State
> {
	private lastResultIsUndefiend = false;
	protected getInitialState(): State {
		const { variant } = this.props;

		return {
			...super.getInitialState(),
			grouping: !isCSOKVariant(variant),
			interestFilter: [],
			interestFilterOptions: INTEREST_FILTER_OPTIONS,
		} as unknown as State;
	}

	public componentDidMount(): void {
		this.getInterestFilterOptions();
		super.componentDidMount();
	}

	public componentDidUpdate(prevProps: Props): void {
		if (this.props.request !== undefined && this.lastResultIsUndefiend) {
			this.lastResultIsUndefiend = false;
			this.getInterestFilterOptions();
		}
		super.componentDidUpdate(prevProps);
	}

	protected getInterestFilterOptions(): void {
		const {
			results,
			searchParams: { maturity },
		} = this.props;

		if (results === undefined) {
			this.lastResultIsUndefiend = true;

			return;
		}

		const interestFilter = [];
		const interestFilterOptions = INTEREST_FILTER_OPTIONS.map((item) => {
			const check = hasResultInPeriod(maturity, results.normalList, item.value);

			if (check) {
				interestFilter.push(item.value);
			}

			return { ...item, disabled: !check };
		});

		if (interestFilterOptions.toString() !== this.state.interestFilter.toString()) {
			this.setState({
				interestFilter,
				interestFilterOptions,
			});
		}
	}

	protected getDefaultOrder(): MortgageOrderFields {
		const { grouping, interestFilter } = this.state;
		const {
			request: { sort: requestSort },
			sort,
		} = this.props;

		return {
			grouping,
			interestFilter,
			...super.getDefaultOrder(),
			sort: sort || requestSort,
		};
	}

	protected sortOptions: CommonOptions<MortgageSort> = [
		{ label: __("THM"), value: "THM" },
		{
			label: __("Normál havi törlesztő részlet"),
			value: "INDULO_TORLESZTES",
		},
		{ label: __("Teljes visszafizetés"), value: "OSSZES_TORLESZTES" },
	];

	protected renderResult(
		props: AbstractResultItemProps<MortgageSearchParams, MortgageRequest, MortgageResponseItem>,
	): ReactNode {
		return <MortgageResultItem {...props} />;
	}

	protected renderHeader(title: ReactNode): ReactNode {
		const {
			variant,
			highlightedBank,
			request: { interestPeriods },
		} = this.props;
		const { grouping, interestFilterOptions } = this.state;
		const isCsok = isCSOKVariant(variant);

		return (
			<FinalForm<MortgageOrderFields>
				initialValues={this.getDefaultOrder()}
				validateOnBlur={false}
				onSubmit={this.onUpdatePagination}
			>
				{({ handleSubmit }) => (
					<Form className="container result-order" onSubmit={handleSubmit}>
						<div className="result-header mt-5 mb-4 text-center d-none d-sm-block">
							<div className="h2">{title}</div>
						</div>
						{isCsok && (
							<div className="fix-interest">
								<span className="h2 text-center mb-4">
									<mark className="highlight-level-1 px-3 py-0">Végig fix</mark> kamat
								</span>
							</div>
						)}
						<div className="mt-4 d-sm-flex align-items-center">
							{this.sortOptions?.length > 0 && (
								<div className="d-flex align-items-center">
									<div className="font-weight-bold mr-2">{__("Rendezés:")}</div>
									<div>
										<FormSelect
											className="mb-0 d-none d-md-block"
											name="sort"
											prepend={<FontAwesomeIcon icon={fasArrowDownWideShort} />}
											onChange={async () => handleSubmit()}
											options={this.sortOptions}
										/>
										<FormSelect
											className="mb-0 d-md-none"
											name="sort"
											prepend={<FontAwesomeIcon icon={fasArrowDownWideShort} />}
											onChange={async () => handleSubmit()}
											options={this.sortOptions.filter(
												(opt) => opt.value !== "OSSZES_TORLESZTES",
											)}
										/>
									</div>
								</div>
							)}
							{!isCsok && !highlightedBank && (
								<div className="ml-auto">
									<FormCheck
										className="mb-0 mt-3 mt-sm-0"
										type="switch"
										label={__("Csoportosítás")}
										name="grouping"
										onChange={async () => {
											this.sendEvent("BM_switchListing", {
												targetListType: grouping ? "list" : "grouped",
											});

											handleSubmit();
										}}
									/>
								</div>
							)}
						</div>
						{grouping && !highlightedBank && variant !== undefined && (
							<>
								<div className="d-sm-flex interest-filter mt-3 mb-2 border-top border-bottom py-2">
									<FormCheckList
										label={__("Kamatperiódus szűrés:")}
										name="interestFilter"
										onChange={async () => handleSubmit()}
										options={interestFilterOptions}
										optionClassName="text-nowrap"
									/>
								</div>
								<div className="periodus-extra-info">
									{__(
										"*Amennyiben a hitel futamideje nem hosszabb mint a kamatperiódusa, akkor a végig fix– kockázatmentes kategóriába soroljuk.",
									)}
								</div>
							</>
						)}
						{highlightedBank && (
							<div className={`${this.getClassNameByCategory(interestPeriods[0])} result-group`}>
								{this.renderGroupTitle(interestPeriods[0])}
							</div>
						)}
					</Form>
				)}
			</FinalForm>
		);
	}

	public render(): ReactNode {
		const { results, error, progress, highlightedBank } = this.props;
		const { grouping } = this.state;

		if (
			!grouping ||
			progress ||
			error ||
			!results ||
			(results.highlightList.length === 0 && results.normalList.length === 0)
		) {
			return super.render();
		}

		return (
			<div id="result" className="result-container pt-4">
				{highlightedBank ? this.renderBankSpecificList() : this.renderGroupedTotalList()}
				{this.renderAfterListBlock()}
			</div>
		);
	}

	private renderGroupedTotalList(): ReactNode {
		return (
			<>
				{this.renderHighlightList()}
				{this.renderHeader(this.renderNormalListTitle())}
				{this.renderResultGroups()}
			</>
		);
	}

	protected renderNormalListTitle(): ReactNode {
		const {
			request: { loanAmount },
		} = this.props;

		return (
			<HtmlBlock
				html={sprintf(
					__("<strong>%s</strong> lakáshitelre a bankok a következő ajánlatokat kínálják"),
					formatText(loanAmount, "ft"),
				)}
			/>
		);
	}

	protected renderListingOffer(): ReactNode {
		const { searchParams } = this.props;
		const { showListingOffer } = this.state;

		if (searchParams.income >= 450_000 && searchParams.loanAmount >= 20_000_000) {
			return (
				<div className="mb-3" key="listing-offer">
					<LandingContainer bgClassName="bg-trianglify-purple" className=" text-center my-3">
						<div className="text-white h1">
							Akár <span className="text-gold display-3 font-weight-bold">6%</span> alatti kamatszint a
							Bankmonitor ügyfeleinek, online előminősítéssel
						</div>
						<h3 className="text-white mb-4">Egyeztess szakértőnkkel díjmentesen!</h3>

						<Button
							className="border-0 mb-2 bg-gold text-dark font-weight-bold px-5"
							onClick={this.onListingOfferClick}
						>
							Egyedi kamatot szeretnék
						</Button>
					</LandingContainer>
					<Collapse in={showListingOffer}>
						<div className="fluid-block  mt-3">
							<Container>
								<Row className="justify-content-center align-items-center flex-column-reverse flex-lg-row p-3">
									<Col className="text-block" lg={7}>
										<div className="h3  font-weight-bold mb-4">
											Hogyan kaphatok kedvezményt a megjelenített kamatszintekből?
										</div>
										<List itemStyle="tick" variant="gold">
											<List.Item>
												A <strong>Bankmonitor</strong> Magyarország{" "}
												<strong>legnagyobb pénzügyi közvetítő</strong> cége*, így sok esetben
												még a <strong>legalacsonyabb</strong> meghirdetett{" "}
												<strong>kamatszintekre is további kedvezményt</strong> kapunk
												ügyfeleinknek a banktól.
											</List.Item>
											<List.Item>
												<strong>Szakértőnik</strong> az egész országban azon dolgoznak, hogy
												minden hiteligényre <strong>a lehető legkedvezőbb feltételekkel</strong>{" "}
												(kamat és egyéb költségek) rendelkező hitelt biztosítsák.
											</List.Item>
											<List.Item>
												<strong>Az egyedi kamatkedvezmény</strong> minden esetben az{" "}
												<strong>igazolt jövedelem</strong> és egyéb kiemelt jellemnzők{" "}
												<strong>függvényében</strong> biztosítható.
											</List.Item>
											<List.Item>
												<strong>Szakértőink szolgáltatása ingyenes</strong> (törvényi előírások
												alapján kizárólag a bank fizethet kompenzációt a bankfiókok helyett
												elvégzett munka ellenértékeként).
											</List.Item>
											<List.Item>
												Minden
												<strong>
													{" "}
													10 millió forint felvett hitel esetén 1 százalék kamatkedvezmény
													közel 1,5 millió Ft megtakarítást
												</strong>{" "}
												jelent a teljes visszafizetés vonatkozásában.
											</List.Item>
											<List.Item>
												Hitelfelvételednél kiemelten fontos, hogy az{" "}
												<strong>
													összes elérhető támogatást és támogatott hitelt is igénybe vedd
												</strong>
												: ebben is segítünk!
											</List.Item>
										</List>
										<small>
											*A 2022-es évre közzétett nettó árbevételek alapján a pénzpiaci közvetítők
											között.
										</small>
									</Col>
									<Col className="pb-4 pb-lg-0" lg={5} md={8}>
										{showListingOffer && (
											<MortgageLeadForm
												{...this.getListingOfferLeadProps()}
												title="Kérj segítséget szakértőnktől!"
												request={getLeadCalculatorRequest(searchParams)}
											/>
										)}
									</Col>
								</Row>
							</Container>
						</div>
					</Collapse>
					<Ad
						name="970x250 DESKTOP TOP1"
						code="5967603"
						width="970"
						height="250"
						type="desktop"
						priority={99}
					/>
					<Ad
						name="300x250 MOBIL TOP1"
						code="5967598"
						width="300"
						height="250"
						type="non-desktop"
						priority={99}
					/>
				</div>
			);
		}

		return (
			<div className="listing-offer-block mb-3" key="listing-offer">
				<Container className="text-center my-3">
					<div className="h1 mb-3">Egyeztess szakértőnkkel díjmentesen,</div>
					<div className="mb-3">
						<strong>
							hogy választ kapj kérdéseidre, és megtaláljátok a számodra legkedvezőbb konstrukciót!
						</strong>
					</div>
					<Button size="lg" className="text-uppercase" onClick={this.onListingOfferClick}>
						Kapcsolatfelvétel
					</Button>
				</Container>
				<Collapse in={showListingOffer}>
					<div className="fluid-block text-white mt-3">
						<Container>
							<Row className="justify-content-center align-items-center flex-column-reverse flex-lg-row p-3">
								<Col className="text-block" lg={7}>
									<div className="h3 text-white font-weight-bold mb-4">
										Lakáshitellel kapcsolatos kérdések, melyek mindenkit foglalkoztatnak
									</div>
									<List itemStyle="tick" variant="gold">
										<List.Item>
											<strong>Magasnak tartom</strong> a jelenlegi <strong>kamatokat</strong>. Ha
											most veszek fel hitelt, örökre ezt a kamatot és törlesztőrészletet fogom
											fizetni?
										</List.Item>
										<List.Item>
											<strong>Változnak a támogatások</strong> és támogatott hitelek… Hogyan hat
											mindez az én helyzetemre?
										</List.Item>
										<List.Item>
											<strong>Hogyan védekezhetek</strong> az ellen, hogy a lakáskeresés
											megkezdése és a hitelfelvétel között esetlegesen{" "}
											<strong>emelkedő kamatok</strong> megnöveljék a fizetendő törleszőrészletet?
										</List.Item>
										<List.Item>
											Mit jelent számomra a <strong>magas infláció</strong> a hitel
											vonatkozásában?
										</List.Item>
										<List.Item>
											Bizonytalannak tartom a gazdasági környezetet…{" "}
											<strong>Hogyan teljesíthetem a hiteltörlesztést</strong>, ha elveszítem a
											munkahelyemet?
										</List.Item>
										<List.Item>
											<strong>Az önerőm</strong>, amit lakásvásárlásra fordítanék a{" "}
											<strong>jelenlegi lakásom</strong> értéke… Hogyan kezeljem ezt a helyzetet?
										</List.Item>
										<List.Item>
											<strong>Albérletben lakom...</strong> Hogyan hat a jelenlegi környezet a
											lakásvásárlási képességemre?
										</List.Item>
									</List>
									<p>Kérdésed van, tedd fel szakértőnknek!</p>
								</Col>
								<Col className="pb-4 pb-lg-0" lg={5} md={8}>
									<MortgageLeadForm
										{...this.getListingOfferLeadProps()}
										title="Kérj segítséget szakértőnktől!"
										request={getLeadCalculatorRequest(searchParams)}
									/>
								</Col>
							</Row>
						</Container>
					</div>
				</Collapse>
			</div>
		);
	}

	protected renderBankSpecifictTitle(): ReactNode {
		const {
			highlightedBank,
			request: { loanAmount },
		} = this.props;

		return (
			<>
				{getBankName(highlightedBank)}{" "}
				<strong className="text-secondary">{formatText(loanAmount, "ft")}</strong> lakáshitelre vonatkozó
				ajánlatai
			</>
		);
	}

	protected renderOtherBankTitle(): ReactNode {
		const {
			request: { loanAmount },
		} = this.props;

		return (
			<>
				A legjobb találatok <strong className="text-secondary">{formatText(loanAmount, "ft")}</strong>{" "}
				lakáshitelre
			</>
		);
	}

	private renderResultGroups(): ReactNode {
		const { interestFilterOptions, interestFilter } = this.state;
		let index = 0;

		return (
			<>
				{interestFilterOptions.map((item) => {
					if (!item.disabled) {
						if (interestFilter.includes(item.value)) {
							++index;
						}

						if (index === 3) {
							return (
								<div key={`offer_${index}`}>
									{this.renderResultGroup(item.value, index)}
									{this.renderAds1()}
								</div>
							);
						}

						return this.renderResultGroup(item.value, index);
					}

					return null;
				})}
				{this.renderAds2()}
			</>
		);
	}

	private renderResultGroup(category: PeriodCategory, groupIndex: number): ReactNode {
		const {
			results,
			searchParams: { maturity },
		} = this.props;
		const { interestFilter } = this.state;

		if (!interestFilter.includes(category)) {
			return null;
		}
		const slicedResults: MortgageResponseItem[] = getResultsInPeriod(maturity, results.normalList, category);

		if (slicedResults.length === 0) {
			return null;
		}
		this.sendViewItemListEvent(
			slicedResults,
			"product",
			"view_item_list",
			`group-interest-${category.toLowerCase()}`,
		);

		const className = this.getClassNameByCategory(category);

		return (
			<div className={`${className} result-group`} key={`result-group_${category}`}>
				{this.renderGroupTitle(category)}
				{this.renderCustomizationAlert()}
				<div className="result-group-list">
					{slicedResults.slice(0, GROUP_LIMIT).map((result, index) => (
						<Container key={`${category}_${result.id || index}`}>
							{this.renderResult({
								...this.getResultProps(result, index + 1),
								groupName: `group-interest-${category.toLowerCase()}`,
								groupIndex,
							})}
						</Container>
					))}
				</div>
				{slicedResults.length > GROUP_LIMIT && (
					<CollapseBlock
						name={className}
						variant="custom_just_open"
						title={
							<Container className="buttons text-center">
								<button type="button" className="show-more mb-3">
									{__("Mutass többet")}{" "}
									<FontAwesomeIcon icon={fasArrowRight} className="ml-2" rotation={90} />
								</button>
							</Container>
						}
					>
						<div className="result-group-list">
							{slicedResults.slice(GROUP_LIMIT).map((result, index) => (
								<Container key={`${category}_${result.id || index}`}>
									{this.renderResult({
										...this.getResultProps(result, index + GROUP_LIMIT + 1),
										groupName: `group-interest-${category.toLowerCase()}`,
										groupIndex,
									})}
								</Container>
							))}
						</div>
					</CollapseBlock>
				)}
				{groupIndex === 1 && this.renderListingOffer()}
			</div>
		);
	}

	protected getResultProps(result: MortgageResponseItem, index: number): MortgageResultItemProps {
		const { variant } = this.props;

		return {
			...super.getResultProps(result, index),
			variant,
		};
	}

	private renderGroupTitle(category: string): ReactNode {
		let name = "";
		let interval = "";

		if ("THREE_MONTHS,SIX_MONTHS,ONE_YEAR,THREE_YEARS".includes(category)) {
			name = __("Változó");
			interval = __("kamat");
		} else if ("FIVE_YEARS".includes(category)) {
			name = `5 ${__("évig")}`;
			interval = __("fix kamat");
		} else if ("TEN_YEARS".includes(category)) {
			name = `10 ${__("évig")}`;
			interval = __("fix kamat");
		} else if ("FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS".includes(category)) {
			name = `15-30 ${__("évig")}`;
			interval = __("fix kamat");
		} else if ("FULL".includes(category)) {
			name = __("Végig");
			interval = __("fix kamat");
		}

		if (getLocale() === "hu_HU") {
			return (
				<span className="h2 text-center mt-5 mb-3">
					<span>{name}</span> {interval}
				</span>
			);
		}

		return (
			<span className="h2 text-center mt-5 mb-3">
				{interval} <span className="text-lowercase">{name}</span>
			</span>
		);
	}

	protected renderError(): ReactNode {
		const { error = "Sajnos a megadott beállítások mellett nincs elérhető ajánlat!", variant } = this.props;
		const isBaseVariant = variant === undefined;

		if (isBaseVariant) {
			return (
				<Alert icon={<FontAwesomeIcon icon={fasCircleXmark} />} size="lg" variant="warning" alignItems="start">
					<div className="mb-3">{error}</div>
					<div className="mb-2">
						<strong>Kíváncsi vagy, hogy mennyi hitelt vehetsz fel?</strong>
					</div>
					<Button
						icon={<FontAwesomeIcon icon={fasCalculatorSimple} />}
						variant="outline-dark"
						href="/lakashitel-kalkulator/mennyi-lakashitelt-kaphatok/?link_type=in-ko-alert"
					>
						Mennyi hitelt kaphatok?
					</Button>
				</Alert>
			);
		}

		return super.renderError();
	}

	@autobind
	protected onUpdatePagination(values: MortgageOrderFields, form: FormApi<any>): void {
		const { grouping: oldGrouping, interestFilter: oldInterestFilter } = this.state;
		const { grouping, interestFilter } = values;

		if (oldGrouping !== grouping) {
			this.setState({
				grouping,
				page: 1,
			});
		}

		if (oldInterestFilter !== interestFilter) {
			// interestFilterOptions
			this.setState({
				interestFilter,
			});
		}

		super.onUpdatePagination(values, form);
	}

	private getClassNameByCategory(category: string): string {
		if ("THREE_MONTHS,SIX_MONTHS,ONE_YEAR,THREE_YEARS".includes(category)) {
			return "short-interest";
		}

		if ("FIVE_YEARS".includes(category)) {
			return "five-interest";
		}

		if ("TEN_YEARS".includes(category)) {
			return "ten-interest";
		}

		if ("FIFTEEN_YEARS,TWENTY_YEARS,TWENTY_FIVE_YEARS,THIRTY_YEARS".includes(category)) {
			return "long-interest";
		}

		if ("FULL".includes(category)) {
			return "fix-interest";
		}

		return undefined;
	}

	protected createViewItemListEventItem(result: MortgageResponseItem): Record<string, any> {
		const {
			searchParams: { loanAmount },
		} = this.props;

		return {
			coupon: "none",
			currency: "HUF",
			discount: result.bmDiscounts?.totalSavings ? result.bmDiscounts.totalSavings : 0,
			item_category: result.installmentStart,
			item_category2: result.fullPayableAmount,
			item_category3: "12", // türelmi idő
			item_category4: result.startUpCostWithoutDiscounts,
			item_category5: loanAmount,
			item_variant: "",
			price: result.apr,
			quantity: Math.round(
				((result.interestPeriodAided ? result.interestAided : result.interestNotAided) || 0) * 10_000,
			),
		};
	}

	protected getComponentName(): string {
		return "MortgageResultList";
	}
}
